import React from 'react';
import { Tooltip } from '../Tooltip';
import Spinner from '../migratorDashboard/Spinner';
import { RESOURCE_TYPE_CATEGORIES, getTypeLabel, getTypeLabelByApiName, DIVISION_SCOPED_RESOURCE_TYPES, DIVISION_ORG_UNIQUE_RESOURCE_TYPES, getResourceCaveats, isTypeSnapshotOnly } from '../../utils/resourceTypes';
import { formatDependencyHint } from '../../utils/resourceDependencyHints';
import { ui } from '../../config/uiSizes';

// Memoized resource item to prevent unnecessary re-renders
const ResourceItem = React.memo<{
  resource: { id: string; name: string; type: string; isPublished?: boolean; flowType?: string };
  isSelected: boolean;
  onToggle: (id: string) => void;
  snapshotContext?: boolean;
}>(({ resource, isSelected, onToggle, snapshotContext = false }) => (
  <div
    data-testid="resource-picker-item"
    className={`w-full p-1 pl-2 flex items-center ${ui.gap.md} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded`}
  >
    <input
      id={`res-${resource.id}`}
      type="checkbox"
      checked={isSelected}
      onChange={() => onToggle(resource.id)}
      className="h-5 w-5 text-emerald-600 border-gray-300 rounded focus:ring-2 focus:ring-emerald-400"
    />
    <label
      htmlFor={`res-${resource.id}`}
      title={resource.name}
      className={`${ui.text.label} text-gray-800 dark:text-gray-100 min-w-0 truncate flex-1 cursor-pointer`}
    >
      {resource.name}
    </label>
    {resource.flowType ? (
      <span
        className={`${ui.badge.sm} ${ui.text.badge} shrink-0 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300`}
      >
        {resource.flowType}
      </span>
    ) : null}
    {typeof resource.isPublished === 'boolean' ? (
      <span
        title={
          resource.isPublished
            ? 'Published — this flow can be migrated.'
            : snapshotContext
              ? 'Draft (no published version). A snapshot still captures it as a supplemental artifact, but a migration would export nothing.'
              : 'Draft (no published version). Only published flows migrate — this would export nothing. Publish it first.'
        }
        className={`${ui.badge.sm} ${ui.text.badge} shrink-0 rounded-full ${resource.isPublished
            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200'
            : 'bg-amber-100 text-amber-700 dark:bg-amber-800 dark:text-amber-100'
          }`}
      >
        {resource.isPublished ? 'Published' : 'Draft'}
      </span>
    ) : null}
  </div>
));

ResourceItem.displayName = 'ResourceItem';

interface ResourceSelectorProps {
  resourceType: string;
  onResourceTypeChange: (value: string) => void;
  selectedResourceTypes?: string[];
  onSelectedResourceTypesChange?: (types: string[]) => void;

  resourceSearch: string;
  onResourceSearchChange: (value: string) => void;

  resourcesDropdownOpen: boolean;
  setResourcesDropdownOpen: (open: boolean) => void;

  uniqueResourceTypes: string[];
  loadingResources: boolean;
  fetchProgress?: { completed: number; total: number };
  fetchError?: string | null;
  organizationSelected: boolean;

  searchedResources: { id: string; name: string; type: string; isPublished?: boolean; flowType?: string }[];
  selectedResourceIds: string[];
  onToggleResource: (id: string) => void;
  onSelectAll: (ids: string[]) => void;

  dropdownRef: React.RefObject<HTMLDivElement>;
  dropdownButtonRef?: React.RefObject<HTMLButtonElement>;
  panelRef?: React.RefObject<HTMLDivElement>;

  onFetchResources?: () => void;
  hasPendingTypes?: boolean;
  /** When true, shows Division-scoped / Org-wide scope badges next to each resource type */
  isD2D?: boolean;
  /**
   * When true, each selected resource type shows an honest preview of the
   * dependency resource kinds that will be auto-added on export (ties to the
   * "Include Dependencies" toggle). Exact instance counts are discovered by the
   * exporter, so only the dependency *kinds* are surfaced here.
   */
  includeDependencies?: boolean;
  /**
   * When true, this selector is used for a snapshot (backup) rather than a migration. Snapshots
   * capture draft flows as supplemental artifacts, so the Draft badge messaging is softened.
   */
  snapshotContext?: boolean;
}

export const ResourceSelector: React.FC<ResourceSelectorProps> = ({
  resourceType,
  selectedResourceTypes = [],
  onSelectedResourceTypesChange,
  resourceSearch,
  onResourceSearchChange,
  resourcesDropdownOpen,
  setResourcesDropdownOpen,
  loadingResources,
  fetchProgress,
  fetchError,
  organizationSelected,
  searchedResources,
  selectedResourceIds,
  onToggleResource,
  onSelectAll,
  dropdownButtonRef,
  dropdownRef,
  panelRef,
  onFetchResources,
  hasPendingTypes = false,
  isD2D = false,
  includeDependencies = false,
  snapshotContext = false
}) => {
  const [typeDropdownOpen, setTypeDropdownOpen] = React.useState(false);
  const [typeSearch, setTypeSearch] = React.useState('');
  // "Published only" quick filter: only Architect flows have a publish state, so a draft is hidden
  // when on. Non-flow resources (isPublished undefined) are always kept. Migration context only.
  const [showPublishedOnly, setShowPublishedOnly] = React.useState(false);
  // D2D mode: when false (default), the dropdown only shows division-scoped + org-wide types.
  // When true, all other types are visible but rendered disabled (grayed-out, not selectable).
  const [showAllTypesD2D, setShowAllTypesD2D] = React.useState(false);
  const [typeTipDismissed, setTypeTipDismissed] = React.useState(false);
  const typeDropdownRef = React.useRef<HTMLDivElement>(null);
  const typeButtonRef = React.useRef<HTMLButtonElement>(null);

  // Helper: in D2D mode, only division-scoped or org-wide types are selectable.
  const isTypeAllowedInD2D = React.useCallback(
    (type: string) =>
      DIVISION_SCOPED_RESOURCE_TYPES.includes(type) ||
      DIVISION_ORG_UNIQUE_RESOURCE_TYPES.includes(type),
    []
  );

  // Explains why org-scoped types are grayed out in a Division-to-Division migration:
  // they live at the organization level and aren't tied to a division, so moving them
  // would affect the whole org rather than a single division. Org-to-Org migrations
  // are the correct path for these types.
  const D2D_UNAVAILABLE_REASON =
    'Org-scoped resource type: this lives at the organization level and is not tied to a ' +
    'division, so it cannot be included in a Division-to-Division migration. Use an ' +
    'Org-to-Org migration to move it.';

  // Optimize lookup performance by converting array to Set
  const selectedIdsSet = React.useMemo(() => new Set(selectedResourceIds), [selectedResourceIds]);

  // Only Architect flows carry a publish state; the "Published only" filter is offered only when the
  // current results contain at least one flow, and never in snapshot context (drafts are captured there).
  const hasFlows = React.useMemo(
    () => searchedResources.some((r) => typeof r.isPublished === 'boolean'),
    [searchedResources]
  );

  const visibleResources = React.useMemo(
    () =>
      showPublishedOnly && !snapshotContext
        ? searchedResources.filter((r) => r.isPublished !== false)
        : searchedResources,
    [searchedResources, showPublishedOnly, snapshotContext]
  );

  // Group resources by type for dropdown display when multiple types are selected
  // Optimized: Only recalculate if dropdown is open and data changes
  const resourcesByTypeGroups = React.useMemo(() => {
    if (!resourcesDropdownOpen) return new Map();

    const groups = new Map<string, typeof searchedResources>();
    for (const resource of visibleResources) {
      const existing = groups.get(resource.type);
      if (existing) {
        existing.push(resource);
      } else {
        groups.set(resource.type, [resource]);
      }
    }
    return groups;
  }, [visibleResources, resourcesDropdownOpen]);

  // Close type dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        typeDropdownRef.current &&
        !typeDropdownRef.current.contains(event.target as Node) &&
        typeButtonRef.current &&
        !typeButtonRef.current.contains(event.target as Node)
      ) {
        setTypeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear search when the type dropdown closes
  React.useEffect(() => {
    if (!typeDropdownOpen) setTypeSearch('');
  }, [typeDropdownOpen]);

  // Clear resource search when the resources dropdown closes
  React.useEffect(() => {
    if (!resourcesDropdownOpen) onResourceSearchChange('');
  }, [resourcesDropdownOpen, onResourceSearchChange]);

  // Filter categories and types based on search
  const filteredCategories = React.useMemo(() => {
    const searchLower = typeSearch.trim().toLowerCase();

    return RESOURCE_TYPE_CATEGORIES.map((category) => {
      let types = category.types;

      // In D2D mode, hide non-allowed types unless the user opts into showing them
      if (isD2D && !showAllTypesD2D) {
        types = types.filter((t) => isTypeAllowedInD2D(t));
      }

      // Snapshot-only types (e.g. the DNS-owned Email Domain) are hidden from a migration/D2D
      // picker but stay available in a snapshot (backup) context.
      if (!snapshotContext) {
        types = types.filter((t) => !isTypeSnapshotOnly(t));
      }

      // Apply search filter
      if (searchLower) {
        types = types.filter((type) =>
          type.toLowerCase().includes(searchLower) ||
          (category.labels?.[type]?.toLowerCase().includes(searchLower) ?? false)
        );
      }

      return { ...category, types };
    }).filter((category) => category.types.length > 0);
  }, [typeSearch, isD2D, showAllTypesD2D, isTypeAllowedInD2D, snapshotContext]);

  const handleTypeCheckboxToggle = (type: string) => {
    if (!onSelectedResourceTypesChange) return;

    const newTypes = selectedResourceTypes.includes(type)
      ? selectedResourceTypes.filter((t) => t !== type)
      : [...selectedResourceTypes, type];

    onSelectedResourceTypesChange(newTypes);
  };

  // The types within a category the user may act on, honoring both the D2D allow-list and the
  // snapshot-only exclusion (so "select all" never adds a type that isn't offered in this mode).
  const selectableTypesIn = React.useCallback(
    (categoryTypes: string[]) => {
      let types = isD2D ? categoryTypes.filter(isTypeAllowedInD2D) : categoryTypes;
      if (!snapshotContext) {
        types = types.filter((t) => !isTypeSnapshotOnly(t));
      }
      return types;
    },
    [isD2D, isTypeAllowedInD2D, snapshotContext]
  );

  const handleCategoryCheckboxToggle = (categoryTypes: string[]) => {
    if (!onSelectedResourceTypesChange) return;

    // Only act on types offered in the current mode (D2D allow-list + snapshot-only exclusion)
    const actionableTypes = selectableTypesIn(categoryTypes);
    if (actionableTypes.length === 0) return;

    const allSelected = actionableTypes.every((type) => selectedResourceTypes.includes(type));

    if (allSelected) {
      // Uncheck all actionable types in this category
      const newTypes = selectedResourceTypes.filter((type) => !actionableTypes.includes(type));
      onSelectedResourceTypesChange(newTypes);
    } else {
      // Check all actionable types in this category
      const typesToAdd = actionableTypes.filter((type) => !selectedResourceTypes.includes(type));
      onSelectedResourceTypesChange([...selectedResourceTypes, ...typesToAdd]);
    }
  };

  const isCategoryFullySelected = (categoryTypes: string[]) => {
    const actionable = selectableTypesIn(categoryTypes);
    return actionable.length > 0 && actionable.every((type) => selectedResourceTypes.includes(type));
  };

  const isCategoryPartiallySelected = (categoryTypes: string[]) => {
    const actionable = selectableTypesIn(categoryTypes);
    return actionable.some((type) => selectedResourceTypes.includes(type)) && !isCategoryFullySelected(categoryTypes);
  };

  return (
    <div className="space-y-6">
      <div className={`flex flex-col sm:flex-row items-center ${ui.gap.md}`}>
        <div className={`w-48 flex items-center ${ui.text.formSectionLabel} font-semibold text-gray-800 dark:text-gray-100`}>
          Resource Type:
          <Tooltip
            content="Choose a resource category to display only resources of that type. Then select individual items from the Resources list."
            position="right"
          />
        </div>
        <div className="flex-1 relative">
          <button
            type="button"
            ref={typeButtonRef}
            onClick={() => organizationSelected && setTypeDropdownOpen(!typeDropdownOpen)}
            className={`w-full text-left text-sm flex items-center justify-between ${ui.input.formDropdown} border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${!organizationSelected ? 'opacity-60 cursor-not-allowed' : ''}`}
            disabled={!organizationSelected}
            title={!organizationSelected ? 'Select an organization first' : ''}
          >
            <span className={`truncate ${!resourceType && !organizationSelected ? 'text-gray-400 dark:text-gray-500' : !resourceType ? 'text-gray-600 dark:text-gray-400' : ''}`}>
              {!organizationSelected
                ? 'Select an organization first'
                : selectedResourceTypes.length > 0
                  ? `${selectedResourceTypes.length} type${selectedResourceTypes.length > 1 ? 's' : ''} selected`
                  : resourceType || (loadingResources ? 'Loading resource types...' : 'Select resource type(s)')}
            </span>
            <span className="ml-2 text-gray-600 dark:text-gray-400">▾</span>
          </button>

          {typeDropdownOpen && (
            <div
              ref={typeDropdownRef}
              className={`absolute z-50 bottom-full mb-2 ${ui.widthAndHeight.resourcePickerPanel} border border-gray-500 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg flex flex-col`}
            >
              {!organizationSelected ? (
                <div className={`px-4 py-6 text-center ${ui.text.label} text-red-600 dark:text-red-400`}>
                  Please select an organization before choosing resource types.
                </div>
              ) : (
                <>
                  {!typeTipDismissed && (
                    <div className={`px-3 pt-2 pb-1 flex-shrink-0 flex items-start ${ui.gap.sm}`}>
                      <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400 flex-1`}>
                        Tip: Search or expand categories to find and select a resource type. Click outside to close.
                      </p>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setTypeTipDismissed(true); }}
                        className={`${ui.text.xs} text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 shrink-0 leading-none`}
                        title="Dismiss tip"
                        aria-label="Dismiss tip"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                  <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search resource types..."
                        value={typeSearch}
                        onChange={(e) => setTypeSearch(e.target.value)}
                        className={`w-full ${ui.input.custom} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${ui.text.label} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        onClick={(e) => e.stopPropagation()}
                      />
                      {typeSearch && (
                        <button
                          type="button"
                          onClick={() => setTypeSearch('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                    {isD2D && (
                      <label className={`mt-2 flex items-center ${ui.gap.sm} cursor-pointer select-none`} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={showAllTypesD2D}
                          onChange={(e) => setShowAllTypesD2D(e.target.checked)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-400"
                        />
                        <span className={`${ui.text.xs} text-gray-600 dark:text-gray-300`}>
                          Show non-division-aware resource types (read-only)
                        </span>
                      </label>
                    )}
                  </div>

                  <div className="overflow-y-auto flex-1 min-h-0">
                    {filteredCategories.length === 0 ? (
                      <div className={`px-4 py-3 ${ui.text.label} text-gray-500 dark:text-gray-400`}>
                        No resource types found
                      </div>
                    ) : (
                      filteredCategories.map((category) => (
                        <details key={category.name} className="group border-b border-gray-200 dark:border-gray-700 last:border-b-0" open={!!typeSearch.trim()}>
                          <summary className="px-4 py-2.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between">
                            <div className={`flex items-center ${ui.gap.md} flex-1`}>
                              <input
                                type="checkbox"
                                checked={isCategoryFullySelected(category.types)}
                                ref={(el) => {
                                  if (el) {
                                    el.indeterminate = isCategoryPartiallySelected(category.types);
                                  }
                                }}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  if (organizationSelected) handleCategoryCheckboxToggle(category.types);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className={`h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-400 ${!organizationSelected ? 'opacity-60 cursor-not-allowed' : ''}`}
                                disabled={!organizationSelected}
                              />
                              <span className={`${ui.text.label} font-semibold text-gray-700 dark:text-gray-300`}>
                                {category.name}
                              </span>
                            </div>
                            <div className={`flex items-center ${ui.gap.sm}`}>
                              <span className={`${ui.badge.sm} text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-full`}>
                                {category.types.length}
                              </span>
                              <svg
                                className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          </summary>
                          <div className="bg-gray-50 dark:bg-gray-900">
                            {category.types.map((type) => {
                              const d2dDisabled = isD2D && !isTypeAllowedInD2D(type);
                              const rowDisabled = !organizationSelected || d2dDisabled;
                              const isTypeSelected = selectedResourceTypes.includes(type);
                              const dependencyHint =
                                includeDependencies && isTypeSelected && !d2dDisabled
                                  ? formatDependencyHint(type)
                                  : null;
                              const caveats = getResourceCaveats(type);
                              return (
                                <div key={type}>
                                  <div
                                    className={`w-full flex items-center ${ui.gap.md} px-6 py-2 ${ui.text.label} transition ${d2dDisabled ? 'cursor-not-allowed bg-gray-50 dark:bg-gray-800/20' : 'hover:bg-gray-100 dark:hover:bg-gray-800'} ${!organizationSelected ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    title={d2dDisabled ? D2D_UNAVAILABLE_REASON : undefined}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedResourceTypes.includes(type)}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        if (!rowDisabled) handleTypeCheckboxToggle(type);
                                      }}
                                      className={`h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-400 ${d2dDisabled ? 'opacity-50 cursor-not-allowed' : ''} ${rowDisabled && !d2dDisabled ? 'cursor-not-allowed' : ''}`}
                                      disabled={rowDisabled}
                                    />
                                    <label
                                      className={`flex-1 ${rowDisabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${selectedResourceTypes.includes(type)
                                        ? 'text-blue-700 dark:text-blue-400 font-medium'
                                        : d2dDisabled
                                          ? 'text-gray-500 dark:text-gray-500'
                                          : 'text-gray-700 dark:text-gray-300'
                                        }`}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        if (!rowDisabled) handleTypeCheckboxToggle(type);
                                      }}
                                    >
                                      {getTypeLabel(category, type)}
                                    </label>
                                    {isD2D && DIVISION_SCOPED_RESOURCE_TYPES.includes(type) && (
                                      <span
                                        className={`shrink-0 inline-flex items-center gap-1.5 ${ui.text.xs} font-medium tracking-tight text-emerald-700 dark:text-emerald-300`}
                                        title="This resource type is division-scoped — a new copy will be created in the target division"
                                      >
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400" aria-hidden="true" />
                                        Will be copied
                                      </span>
                                    )}
                                    {isD2D && DIVISION_ORG_UNIQUE_RESOURCE_TYPES.includes(type) && (
                                      <span
                                        className={`shrink-0 inline-flex items-center gap-1.5 ${ui.text.xs} font-medium tracking-tight text-amber-700 dark:text-amber-300`}
                                        title="This resource type has an org-wide unique name and will be reassigned to the target division — not duplicated"
                                      >
                                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 dark:bg-amber-400" aria-hidden="true" />
                                        Will be reassigned
                                      </span>
                                    )}
                                    {d2dDisabled && (
                                      <span
                                        className={`shrink-0 inline-flex items-center gap-1.5 ${ui.text.xs} font-medium tracking-tight text-gray-400 dark:text-gray-500`}
                                        title={D2D_UNAVAILABLE_REASON}
                                      >
                                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600" aria-hidden="true" />
                                        Unavailable
                                      </span>
                                    )}
                                    {!d2dDisabled &&
                                      caveats.map((caveat) => {
                                        const tone =
                                          caveat.tone === 'warning'
                                            ? { text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-500 dark:bg-amber-400' }
                                            : caveat.tone === 'positive'
                                              ? { text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-500 dark:bg-emerald-400' }
                                              : { text: 'text-blue-700 dark:text-blue-300', dot: 'bg-blue-400 dark:bg-blue-300' };
                                        return (
                                          <span
                                            key={caveat.kind}
                                            className={`shrink-0 inline-flex items-center ${ui.gap.xs} ${ui.text.xs} font-medium tracking-tight ${tone.text}`}
                                            title={caveat.tooltip}
                                          >
                                            <span className={`${ui.widthAndHeight.badgeDot} rounded-full ${tone.dot}`} aria-hidden="true" />
                                            {caveat.label}
                                          </span>
                                        );
                                      })}
                                  </div>
                                  {dependencyHint && (
                                    <div
                                      className={`px-6 pl-14 pb-2 flex items-start ${ui.gap.xs} ${ui.text.xs} text-blue-600 dark:text-blue-300`}
                                      title="These dependency kinds are auto-added when Include Dependencies is on. The exporter discovers the exact instances."
                                    >
                                      <span className="w-1.5 h-1.5 mt-1 rounded-full bg-blue-400 dark:bg-blue-300 shrink-0" aria-hidden="true" />
                                      <span>{dependencyHint}</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </details>
                      ))
                    )}
                  </div>

                  {/* Info note when both datatable types are selected */}
                  {selectedResourceTypes.includes('Architect Datatable') &&
                    selectedResourceTypes.includes('Architect Datatable (with Data)') && (
                      <div className={`mx-3 my-2 p-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-md flex items-start ${ui.gap.sm}`}>
                        <svg className={`${ui.svg.sm} text-blue-500 mt-0.5 flex-shrink-0`} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <span className={`${ui.text.xs} text-blue-700 dark:text-blue-300`}>
                          <strong>Data Table (with Data)</strong> already includes the table schema. Selecting <strong>Data Table Schema</strong> separately is not needed — those tables will not be exported twice.
                        </span>
                      </div>
                    )}

                  {/* Load Resources Button inside dropdown */}
                  {selectedResourceTypes && selectedResourceTypes.length > 0 && hasPendingTypes && (
                    <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                      <p className={`${ui.text.xs} text-green-700 dark:text-green-300 font-medium ${ui.mb.sm} flex items-center ${ui.gap.xs}`}>
                        <svg className={ui.svg.sm} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                        Almost there — click below to load the resources for your selected type(s).
                      </p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onFetchResources) {
                            onFetchResources();
                            setTypeDropdownOpen(false);
                          }
                        }}
                        disabled={loadingResources || !organizationSelected}
                        className={`w-full ${ui.button.md} rounded-lg font-semibold shadow-md transition flex items-center justify-center ${ui.gap.sm} ${loadingResources || !organizationSelected
                          ? 'bg-gray-400 dark:bg-gray-600 text-gray-200 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 text-white cursor-pointer ring-2 ring-green-400/70 dark:ring-green-500/60 ring-offset-2 ring-offset-white dark:ring-offset-gray-800'
                          }`}
                      >
                        {loadingResources ? (
                          <>
                            <svg className={`animate-spin ${ui.svg.sm} text-white`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Loading Resources...</span>
                          </>
                        ) : (
                          <>
                            <svg className={ui.svg.sm} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            <span>Load Resources ({selectedResourceTypes.length} type{selectedResourceTypes.length > 1 ? 's' : ''})</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Resources dropdown with checkboxes */}
      <div className={`flex flex-col sm:flex-row items-center ${ui.gap.md}`}>
        <div className={`w-48 flex items-center ${ui.text.formSectionLabel} font-semibold text-gray-800 dark:text-gray-100`}>
          Resources:
          <Tooltip
            content="Choose specific resources to migrate or use 'Select all that apply'."
            position="right"
          />
        </div>

        <div className="flex-1 relative" ref={dropdownRef}>
          <button
            type="button"
            ref={dropdownButtonRef}
            onClick={() => setResourcesDropdownOpen(!resourcesDropdownOpen)}
            className={`w-full text-left text-sm flex items-center justify-between ${ui.input.formDropdown} border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
            aria-haspopup="listbox"
            aria-expanded={resourcesDropdownOpen}
          >
            <span
              className={`truncate ${!resourceType
                ? 'text-gray-400 dark:text-gray-500'
                : selectedResourceIds.length === 0 && !organizationSelected
                  ? 'text-gray-400 dark:text-gray-500'
                  : selectedResourceIds.length === 0
                    ? 'text-gray-600 dark:text-gray-400'
                    : ''
                }`}
            >
              {loadingResources && fetchProgress && fetchProgress.total > 0
                ? selectedResourceIds.length > 0
                  ? `${selectedResourceIds.length} selected - Loading... (${fetchProgress.completed}/${fetchProgress.total} types)`
                  : `Loading... (${fetchProgress.completed}/${fetchProgress.total} types)`
                : selectedResourceIds.length === 0
                  ? 'Select resources'
                  : `${selectedResourceIds.length} selected`}
            </span>
            <span className="ml-2 text-gray-600 dark:text-gray-400">▾</span>
          </button>

          {resourcesDropdownOpen && (
            <div
              ref={panelRef}
              className={`absolute z-50 bottom-full mb-2 ${ui.widthAndHeight.resourcePickerPanel} border border-gray-700 dark:border-gray-500 bg-white dark:bg-gray-800 p-3 ${ui.text.label} overflow-auto shadow`}
              role="listbox"
              aria-label="Resources"
            >
              <p className={`${ui.text.xs} text-gray-600 dark:text-gray-400 mb-2`}>
                Tip: click checkboxes to select resources; click outside to close.
              </p>
              <input
                type="text"
                placeholder="Search resources..."
                value={resourceSearch}
                onChange={(e) => onResourceSearchChange(e.target.value)}
                className={`w-full ${ui.input.custom} mb-3 border-2 border-gray-400 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${ui.text.label} focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />

              {/* Collapsible Error Banner */}
              {fetchError && !loadingResources && (
                <details className="mb-3 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 dark:border-yellow-600 rounded">
                  <summary className={`px-3 py-2 cursor-pointer hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition flex items-center ${ui.gap.sm} list-none`}>
                    <svg className="w-4 h-4 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className={`flex-1 ${ui.text.xs} font-bold text-yellow-800 dark:text-yellow-200`}>
                      Some resource types failed to load
                    </span>
                    <svg className="w-4 h-4 text-yellow-600 dark:text-yellow-400 transition-transform [[details[open]>summary>&]]:rotate-90" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </summary>
                  <div className="px-3 pb-2">
                    <div className={`${ui.text.xs} text-yellow-900 dark:text-yellow-300 space-y-0.5 max-h-32 overflow-y-auto`}>
                      {fetchError.split('\n')
                        .filter(line => line.trim() && !line.includes('Successful Types:'))
                        .map((line, idx) => {
                          const trimmed = line.trim();
                          const isBullet = trimmed.startsWith('•');
                          const displayLine = isBullet
                            ? `• ${getTypeLabelByApiName(trimmed.replace(/^•\s*/, ''))}`
                            : trimmed.replace('Failed Types:', '').trim();
                          return (
                            <div key={idx} className={line.includes('Failed Types:') ? 'font-semibold mt-1 first:mt-0' : ''}>
                              {displayLine}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </details>
              )}

              {!loadingResources && (
                <div className="flex items-center mb-2">
                  <div className="w-full p-2 rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-800">
                    <label htmlFor="selectAllResources" className={`flex items-center ${ui.gap.md}`}>
                      <input
                        id="selectAllResources"
                        type="checkbox"
                        checked={
                          visibleResources.length > 0 &&
                          visibleResources.every((r) => selectedIdsSet.has(r.id))
                        }
                        onChange={() => onSelectAll(visibleResources.map((r) => r.id))}
                        className="h-5 w-5 text-emerald-600 border-gray-300 rounded focus:ring-2 focus:ring-emerald-400"
                      />
                      <span className={`${ui.text.label} font-semibold text-gray-800 dark:text-gray-100`}>
                        Select all that apply
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {!loadingResources && hasFlows && !snapshotContext && (
                <label htmlFor="publishedOnlyFilter" className={`flex items-center ${ui.gap.sm} mb-2 cursor-pointer`}>
                  <input
                    id="publishedOnlyFilter"
                    type="checkbox"
                    checked={showPublishedOnly}
                    onChange={() => setShowPublishedOnly((prev) => !prev)}
                    className="h-4 w-4 text-emerald-600 border-gray-300 rounded focus:ring-2 focus:ring-emerald-400"
                  />
                  <span className={`${ui.text.xs} text-gray-600 dark:text-gray-400`}>
                    Published flows only (drafts export nothing)
                  </span>
                </label>
              )}

              <div className="space-y-2">
                {loadingResources ? (
                  <div className="flex flex-col justify-center items-center py-4 space-y-2">
                    <Spinner />
                    {fetchProgress && fetchProgress.total > 0 && (
                      <div className={`${ui.text.label} text-gray-600 dark:text-gray-400`}>
                        Loading resources... ({fetchProgress.completed}/{fetchProgress.total} types complete)
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {visibleResources.length === 0 ? (
                      <div className={`${ui.text.label} text-gray-500 text-center py-4`}>
                        No resources available{fetchError ? ' from successful types' : ' for this type or matching your search'}.
                      </div>
                    ) : selectedResourceTypes && selectedResourceTypes.length > 1 ? (
                      // Group by type when multiple types are selected
                      Array.from(resourcesByTypeGroups.entries()).map(([type, resources]) => {
                        const allTypeResourcesSelected = resources.every((r: { id: string; name: string; type: string }) => selectedIdsSet.has(r.id));
                        const someTypeResourcesSelected = resources.some((r: { id: string; name: string; type: string }) => selectedIdsSet.has(r.id));

                        return (
                          <details key={type} className="group border border-gray-200 dark:border-gray-700 rounded-lg">
                            <summary className="cursor-pointer px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-t-lg font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 list-none">
                              <div className="flex items-center justify-between">
                                <div className={`flex items-center ${ui.gap.md}`}>
                                  <input
                                    type="checkbox"
                                    checked={allTypeResourcesSelected}
                                    ref={(el) => {
                                      if (el) {
                                        el.indeterminate = someTypeResourcesSelected && !allTypeResourcesSelected;
                                      }
                                    }}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      const resourceIds = resources.map((r: { id: string; name: string; type: string }) => r.id);
                                      if (allTypeResourcesSelected) {
                                        // Deselect all resources of this type
                                        onSelectAll(resourceIds);
                                      } else {
                                        // Select all resources of this type
                                        onSelectAll(resourceIds);
                                      }
                                    }}
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-400"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <span>{getTypeLabelByApiName(type)}</span>
                                </div>
                                <div className={`flex items-center ${ui.gap.sm}`}>
                                  <span className={`${ui.badge.sm} bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full`}>
                                    {resources.length}
                                  </span>
                                  <svg
                                    className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </summary>
                            <div className="px-2 py-1 space-y-1">
                              {resources.map((r: { id: string; name: string; type: string }) => (
                                <ResourceItem
                                  key={r.id}
                                  resource={r}
                                  isSelected={selectedIdsSet.has(r.id)}
                                  onToggle={onToggleResource}
                                  snapshotContext={snapshotContext}
                                />
                              ))}
                            </div>
                          </details>
                        );
                      })
                    ) : (
                      // Flat list when single type is selected - use memoized component
                      visibleResources.map((r) => (
                        <ResourceItem
                          key={r.id}
                          resource={r}
                          isSelected={selectedIdsSet.has(r.id)}
                          onToggle={onToggleResource}
                          snapshotContext={snapshotContext}
                        />
                      ))
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResourceSelector;
